import clsx from 'clsx';
import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import { PrimaryButton } from '@components/Button/variants';
import { ModalHeaderProps } from '@components/MarketingModal/common/modalHeader';
import RecommendedArticles from '@components/SideFeedItems/RecommendedArticles';
import Spinner from '@components/Spinner';
import { TiModalColorScheme } from '@components/TIModal';
import useArticlesBySlug from '@hooks/useArticlesBySlug';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { getSafeUrl } from '@utils/safe_url';

export type MultipleContentTextProps = ModalHeaderProps & {
  articleSlugs?: string[];
  articleTitleSection?: string;
  buttonText?: string;
  customUrl?: string;
  modalId?: string | number;
  modalTheme?: TiModalColorScheme;
};

const MultipleContentText = ({
  articleSlugs,
  articleTitleSection = 'Recommended',
  highlight,
  buttonText,
  customUrl,
  modalId,
  modalTheme = TiModalColorScheme.light,
  modalType,
  subtitle,
  title,
}: MultipleContentTextProps) => {
  const safeArticleSlugs = articleSlugs || [];
  const { data: articles, isLoading } = useArticlesBySlug({ articleSlugs: safeArticleSlugs });
  const { trackEvent } = useEventTracking();
  const safecustomUrl = getSafeUrl(customUrl, { allowRelative: true });

  const handleButtonTextClick = () => {
    trackEvent(
      click({
        clickTarget: ClickTarget.AnchorLink,
        clickText: buttonText,
        clickLink: safecustomUrl,
      })
    );
  };

  const textColor = modalTheme === TiModalColorScheme.dark ? 'text-white' : 'text-primary-black';
  const borderColor =
    modalTheme === TiModalColorScheme.dark ? 'border-grayscale-beige' : 'border-grayscale-gray';

  const utmParams = {
    utm_source: 'bc_modal',
    utm_campaign: modalType,
    utm_content: modalId ? String(modalId) : undefined,
  };

  return (
    <div className="flex max-h-[100vh] min-h-[420px] w-full flex-wrap overflow-y-auto">
      <div
        className={clsx(
          'flex w-full items-center border-0 border-b border-solid px-32 py-64 md:w-[55%] md:border-b-0 md:border-r md:px-48 lg:px-48',
          borderColor
        )}
      >
        <div className="flex max-w-[420px] flex-col gap-y-32">
          <div className="flex flex-col gap-y-16">
            <div>
              {highlight && (
                <span className="block font-suisse-intl-mono text-overline-sm font-normal text-primary-magenta sm:text-overline-md">
                  {highlight}
                </span>
              )}
              <h1
                className={clsx(
                  'my-0 font-suisse-works text-headline-sm sm:text-headline-lg',
                  textColor
                )}
              >
                {title}
              </h1>
            </div>
            <p className={clsx('my-0 font-suisse-intl text-body-md sm:text-body-lg', textColor)}>
              {ReactHtmlParser(subtitle)}
            </p>
          </div>
          <PrimaryButton
            as="link"
            className="my-0 w-fit"
            href={safecustomUrl}
            onClickCapture={handleButtonTextClick}
          >
            {buttonText}
          </PrimaryButton>
        </div>
      </div>
      <div className="flex w-full items-center justify-center px-32 py-48 md:w-[45%] md:px-48">
        <div className="w-full max-w-[330px]">
          {highlight && <span className="sr-only">Recommended articles for {highlight}</span>}
          <div className={clsx('mb-24 border-0 border-b border-solid pb-4', borderColor)}>
            <h2 className="m-0 font-suisse-intl-mono text-overline-sm font-normal text-primary-magenta">
              {articleTitleSection}
            </h2>
          </div>
          {isLoading ? (
            <Spinner />
          ) : (
            <RecommendedArticles
              className={clsx('!mb-24 last:!mb-0', {
                '*:[&_h2]:text-white *:[&_h2~*>*>*]:text-white *:[&_h2~*]:text-white':
                  modalTheme === TiModalColorScheme.dark,
              })}
              recommendedArticles={articles || []}
              skipSectionTitle
              trackingLocation={TrackingLocation.Modal}
              utmParams={utmParams}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MultipleContentText;
